/**
 * Identifier generation.
 *
 * Every primary key is a UUIDv7 generated by the application, never by the database.
 * That is what makes offline billing safe: a terminal that has been disconnected for a day
 * can mint ids with no coordination and they will never collide with the server's.
 */

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

function randomBytes(length: number): Uint8Array {
  const bytes = new Uint8Array(length);
  globalThis.crypto.getRandomValues(bytes);
  return bytes;
}

/** Milliseconds since the Unix epoch. */
function nowMs(): number {
  return Date.now();
}

const encoder = new TextEncoder();

export function newId(): string {
  const bytes = randomBytes(16);
  const ms = nowMs();

  // 48 bits of timestamp
  const timeHigh = Math.floor(ms / 2 ** 16) & 0xffff;
  const timeMid = ms & 0xffff;

  bytes[0] = (timeHigh >>> 8) & 0xff;
  bytes[1] = timeHigh & 0xff;
  bytes[2] = (timeMid >>> 8) & 0xff;
  bytes[3] = timeMid & 0xff;
  // version 7
  bytes[6] = (bytes[6] ?? 0) & 0x0f | 0x70;
  // variant 10xx
  bytes[8] = (bytes[8] ?? 0) & 0x3f | 0x80;

  const hex = [...bytes].map((byte) => byte.toString(16).padStart(2, '0')).join('');
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
}

export function isUuid(value: string | null | undefined): boolean {
  return typeof value === 'string' && UUID_RE.test(value);
}

/** Extract the embedded creation time from a UUIDv7, for ordering offline records. */
export function uuidTimestamp(value: string): number | null {
  if (!isUuid(value)) return null;
  const hex = value.replace(/-/g, '');
  const ms = parseInt(hex.slice(0, 8), 16) * 2 ** 16 + parseInt(hex.slice(8, 12), 16);
  return Number.isFinite(ms) ? ms : null;
}

/** Monotonic tiebreaker so two records minted in the same millisecond still sort stably. */
let lastMs = 0;
let sequence = 0;

export function newMonotonicId(): string {
  const ms = nowMs();
  if (ms === lastMs) {
    sequence = (sequence + 1) & 0x0fff;
  } else {
    lastMs = ms;
    sequence = Math.floor(Math.random() * 0x0fff);
  }
  const bytes = randomBytes(16);
  const timeHigh = Math.floor(ms / 2 ** 16) & 0xffff;
  const timeMid = ms & 0xffff;
  bytes[0] = (timeHigh >>> 8) & 0xff;
  bytes[1] = timeHigh & 0xff;
  bytes[2] = (timeMid >>> 8) & 0xff;
  bytes[3] = timeMid & 0xff;
  bytes[4] = (bytes[4] ?? 0) & 0x0f | 0x70;
  bytes[5] = ((sequence >>> 8) & 0x0f) | 0x70;
  bytes[6] = (sequence & 0xff) | 0x00;
  bytes[7] = ((bytes[7] ?? 0) & 0x3f) | 0x80;
  const hex = [...bytes].map((byte) => byte.toString(16).padStart(2, '0')).join('');
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
}

/**
 * Idempotency key for offline sync. The terminal sends this on every retry so the server
 * can recognise a duplicate and return the original result instead of double-posting a sale.
 */
export function newIdempotencyKey(): string {
  return newId();
}

export function shortCode(length = 6): string {
  const alphabet = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789';
  const bytes = randomBytes(length);
  return [...bytes].map((byte) => alphabet[byte % alphabet.length]).join('');
}

/** Stable key for CSV import row errors so a retry maps to the same row. */
export function rowFingerprint(importId: string, rowNumber: number): string {
  return `${importId}:${rowNumber}`;
}

export function sha256Hex(input: string): Promise<string> {
  return crypto.subtle.digest('SHA-256', encoder.encode(input)).then((buffer) =>
    [...new Uint8Array(buffer)].map((byte) => byte.toString(16).padStart(2, '0')).join(''),
  );
}
